import React, { useEffect, useState } from 'react';
import { differenceInSeconds } from 'date-fns';
import { countdownParts } from '../../../../../../../utils/countdownParts';
import { Box, Typography } from '@mui/material';

type GiveawayCountdownTimerProps = {
  nextGiveaway: Date;
  isTiny?: boolean;
  isDense?: boolean;
  isJumbo?: boolean;
  fieldsToShow?: Array<'days' | 'hours' | 'minutes' | 'seconds'>;
};

export const GiveawayCountdownTimer = ({
  nextGiveaway,
  isTiny = false,
  isDense = false,
  isJumbo = false,
  fieldsToShow = ['days', 'hours', 'minutes', 'seconds'] // Default to all fields
}: GiveawayCountdownTimerProps) => {
  const [countdownTimer, setCountdownTimer] = useState<number>(
    differenceInSeconds(nextGiveaway, new Date())
  );

  const zeroPad = (num: number) => String(num).padStart(2, '0');
  const duration = countdownParts(countdownTimer);

  // Recalculate countdown when nextGiveaway changes
  useEffect(() => {
    const now = new Date();
    const newCountdown = differenceInSeconds(nextGiveaway, now);

    // Ensure we don't set negative countdown
    setCountdownTimer(Math.max(0, newCountdown));
  }, [nextGiveaway]);

  useEffect(() => {
    if (countdownTimer > 0) {
      const timeout = setTimeout(() => {
        setCountdownTimer(countdownTimer - 1);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [countdownTimer]);

  const timerFields = [
    fieldsToShow.includes('days')
      ? { label: 'Days', value: duration.days }
      : null,
    fieldsToShow.includes('hours')
      ? { label: 'Hours', value: duration.hours }
      : null,
    fieldsToShow.includes('minutes')
      ? { label: 'Mins', value: duration.minutes }
      : null,
    fieldsToShow.includes('seconds')
      ? { label: 'Secs', value: duration.seconds }
      : null
  ].filter(Boolean) as Array<{ label: string; value: number | undefined }>;

  const renderField = (
    label: string,
    value: number | undefined,
    isLast: boolean
  ) => (
    <React.Fragment key={label}>
      <Box
        sx={{
          boxSizing: 'border-box',
          /* On a phone the four fields SHARE the row: each takes a quarter of the
             width that is there (flex 1 1 0, no minimum) instead of claiming a
             fixed 78px. With the fixed width, four fields plus three gaps came to
             342px, two more than the 340px this box allowed itself, so on every
             phone up to 414px wide the Secs field dropped onto a second row. From
             sm up the fields keep their minimum widths and may wrap as before. */
          flex: { xs: isTiny ? undefined : '1 1 0', sm: '0 1 auto' },
          width: {
            xs: isTiny ? 'calc((100% - 24px) / 4)' : undefined,
            sm: undefined
          },
          minWidth: {
            xs: 0,
            sm: isDense ? 64 : isTiny ? 88 : isJumbo ? 150 : 110,
            md: isJumbo ? 180 : undefined
          },
          px: {
            xs: isDense ? 0.5 : isTiny ? 1.2 : 0.75,
            sm: isDense ? 1 : isTiny ? 1.6 : isJumbo ? 3 : 2.2,
            md: isJumbo ? 3.8 : undefined
          },
          py: {
            xs: isDense ? 0.85 : isTiny ? 1 : 1.2,
            sm: isDense ? 1 : isTiny ? 1.2 : isJumbo ? 2.2 : 1.6,
            md: isJumbo ? 2.8 : undefined
          },
          borderRadius: { xs: 2.5, sm: 3 },
          background: 'linear-gradient(180deg, #f8fafc 0%, #eef6f8 100%)',
          border: '1px solid rgba(8,145,178,0.14)',
          boxShadow: '0 14px 36px rgba(15,23,42,0.08)',
          textAlign: 'center'
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: isDense ? 22 : isTiny ? 26 : 'clamp(22px, 7.5vw, 30px)',
              sm: isDense ? 28 : isTiny ? 32 : isJumbo ? 58 : 40,
              md: isJumbo ? 76 : undefined
            },
            lineHeight: 1,
            fontWeight: 1000,
            color: '#0f172a',
            fontVariantNumeric: 'tabular-nums'
          }}
        >
          {zeroPad(value ?? 0)}
        </Typography>
        <Typography
          sx={{
            mt: 0.7,
            fontSize: {
              xs: isDense ? 10 : 11,
              sm: isDense ? 10.5 : isJumbo ? 14 : 12,
              md: isJumbo ? 16 : undefined
            },
            letterSpacing: isDense ? '0.14em' : '0.18em',
            textTransform: 'uppercase',
            fontWeight: 900,
            color: '#64748b'
          }}
        >
          {label}
        </Typography>
      </Box>

      {!isLast && (
        <Typography
          sx={{
            alignSelf: 'center',
            display: { xs: 'none', sm: 'block' },
            fontSize: {
              sm: isDense ? 22 : isTiny ? 28 : isJumbo ? 44 : 34,
              md: isJumbo ? 54 : undefined
            },
            fontWeight: 900,
            color: 'rgba(8,145,178,0.5)',
            lineHeight: 1
          }}
        >
          :
        </Typography>
      )}
    </React.Fragment>
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        justifyContent: 'center',
        gap: {
          xs: isDense ? 0.8 : 1,
          sm: isDense ? 1 : isJumbo ? 2.2 : 1.5,
          md: isJumbo ? 3 : undefined
        },
        width: '100%',
        maxWidth: { xs: isTiny ? 600 : 360, sm: 'none' },
        mx: 'auto'
      }}
    >
      {timerFields.map((field, index) =>
        renderField(field.label, field.value, index === timerFields.length - 1)
      )}
    </Box>
  );
};
